import React from 'react';
import { Card, Button, Modal, Form, Input, List, message, Tag, Empty } from 'antd';
import { PlusOutlined, SendOutlined } from '@ant-design/icons';
import api from '../services/api';
import { useIsMobile } from '../hooks/useIsMobile';

const Support: React.FC = () => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = React.useState(false);
  const [tickets, setTickets] = React.useState<any[]>([]);
  const [modalVisible, setModalVisible] = React.useState(false);
  const [form] = Form.useForm();
  const [detailsVisible, setDetailsVisible] = React.useState(false);
  const [selected, setSelected] = React.useState<any | null>(null);
  const [replyText, setReplyText] = React.useState('');
  const [replying, setReplying] = React.useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/support/tickets').then(r => r.data);
      setTickets(data);
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Не удалось загрузить обращения');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { load(); }, []);

  const createTicket = async () => {
    try {
      const values = await form.validateFields();
      await api.post('/api/support/tickets', values);
      message.success('Обращение создано');
      setModalVisible(false);
      form.resetFields();
      load();
    } catch (e: any) {
      message.error('Ошибка при создании обращения');
    }
  };

  const sendReply = async () => {
    if (!selected || !replyText.trim()) {
      message.warning('Введите текст ответа');
      return;
    }

    setReplying(true);
    try {
      await api.post(`/api/support/tickets/${selected.id}/reply`, {
        message: replyText
      });
      message.success('Ответ отправлен');
      setReplyText('');
      await load();
      setSelected(null);
      setDetailsVisible(false);
    } catch (e: any) {
      message.error('Ошибка при отправке ответа');
    } finally {
      setReplying(false);
    }
  };

  const getStatusTag = (status: string) => {
    const colors: { [key: string]: string } = {
      open: 'blue',
      closed: 'green',
      archived: 'red'
    };
    const labels: { [key: string]: string } = {
      open: 'Открыто',
      closed: 'Закрыто',
      archived: 'В архиве'
    };
    return <Tag color={colors[status] || 'gray'}>{labels[status] || status}</Tag>;
  };

  return (
    <Card
      title="Служба поддержки"
      loading={loading}
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setModalVisible(true)}
        >
          Создать обращение
        </Button>
      }
    >
        {tickets.length === 0 ? (
          <Empty
            description="Обращений нет"
            style={{ marginTop: '50px', marginBottom: '50px' }}
          />
        ) : (
          <List
            dataSource={tickets}
            renderItem={(ticket: any) => (
              <List.Item
                key={ticket.id}
                onClick={() => {
                  setSelected(ticket);
                  setDetailsVisible(true);
                }}
                style={{
                  padding: '16px',
                  marginBottom: '12px',
                  border: '1px solid #f0f0f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  backgroundColor: '#fff'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#fafafa';
                  e.currentTarget.style.borderColor = '#1890ff';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(24, 144, 255, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fff';
                  e.currentTarget.style.borderColor = '#f0f0f0';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <List.Item.Meta
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontWeight: 500, fontSize: '15px', color: '#1890ff' }}>
                        #{ticket.id}: {ticket.subject}
                      </span>
                      {getStatusTag(ticket.status)}
                    </div>
                  }
                  description={
                    <div style={{ fontSize: '13px', color: '#666', marginTop: '8px', lineHeight: '1.6' }}>
                      <div>Создана: {new Date(ticket.createdAt).toLocaleString('ru')}</div>
                      {ticket.updatedAt && (
                        <div>Обновлена: {new Date(ticket.updatedAt).toLocaleString('ru')}</div>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}

        {/* Модал создания обращения */}
        <Modal
          title="Новое обращение"
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          okText="Отправить"
          cancelText="Отмена"
          onOk={createTicket}
          width={isMobile ? '100%' : 520}
          centered={!isMobile}
          style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
        >
          <Form layout="vertical" form={form}>
            <Form.Item
              label="Тема"
              name="subject"
              rules={[{ required: true, message: 'Введите тему обращения' }]}
            >
              <Input placeholder="Описание вашей проблемы" />
            </Form.Item>
            <Form.Item
              label="Сообщение"
              name="message"
              rules={[{ required: true, message: 'Введите сообщение' }]}
            >
              <Input.TextArea rows={4} placeholder="Подробно опишите вашу проблему" />
            </Form.Item>
          </Form>
        </Modal>

        {/* Модал деталей обращения */}
        <Modal
          title={`Обращение #${selected?.id}: ${selected?.subject}`}
          open={detailsVisible}
          onCancel={() => {
            setDetailsVisible(false);
            setSelected(null);
            setReplyText('');
          }}
          footer={null}
          width={isMobile ? '100%' : 700}
          centered={!isMobile}
          style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
          styles={{ body: { padding: '20px' } }}
        >
          {selected && (
            <div>
              <div style={{ 
                marginBottom: '20px',
                paddingBottom: '16px',
                borderBottom: '1px solid #f0f0f0'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    Статус: {getStatusTag(selected.status)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    Создана: {new Date(selected.createdAt).toLocaleString('ru')}
                  </div>
                </div>
              </div>

              {/* Сообщения */}
              <div style={{
                maxHeight: '350px',
                overflowY: 'auto',
                marginBottom: '20px',
                padding: '12px',
                background: '#fafafa',
                borderRadius: '8px',
                border: '1px solid #f0f0f0'
              }}>
                {selected.messages && selected.messages.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {selected.messages.map((msg: any, idx: number) => (
                      <div
                        key={idx}
                        style={{
                          padding: '12px',
                          background: msg.isFromUser ? '#e6f7ff' : '#f6f6f6',
                          borderRadius: '6px',
                          borderLeft: `4px solid ${msg.isFromUser ? '#1890ff' : '#999'}`,
                          transition: 'all 0.3s'
                        }}
                      >
                        <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: msg.isFromUser ? '#1890ff' : '#666' }}>
                          {msg.isFromUser ? '👤 Вы' : '💬 Служба поддержки'}
                        </div>
                        <div style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '6px' }}>{msg.message}</div>
                        <div style={{ fontSize: '11px', color: '#999' }}>
                          {new Date(msg.createdAt).toLocaleString('ru')}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', color: '#999', padding: '40px 20px' }}>
                    Нет сообщений
                  </div>
                )}
              </div>

              {/* Поле для ответа */}
              {selected.status !== 'closed' && selected.status !== 'archived' && (
                <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
                  <div style={{ marginBottom: '12px', fontSize: '13px', fontWeight: 500, color: '#666' }}>
                    Ваш ответ:
                  </div>
                  <Input.TextArea
                    rows={3}
                    placeholder="Напишите ответ..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    style={{ marginBottom: '12px', borderRadius: '6px' }}
                  />
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={sendReply}
                    loading={replying}
                    style={{ width: '100%' }}
                    size="large"
                  >
                    Отправить ответ
                  </Button>
                </div>
              )}
            </div>
          )}
        </Modal>
    </Card>
  );
};

export default Support;
